import { createContext, useContext, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Truck, ShoppingBag, BadgePercent, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { SITE } from "../data/site";
import { useLang } from "../i18n";

const OrderContext = createContext(null);
export const useOrder = () => useContext(OrderContext);

export const OrderProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const { t } = useLang();
    return (
        <OrderContext.Provider value={{ openOrder: () => setOpen(true) }}>
            {children}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="order-dialog"
                    className="bg-onyx border-brass/25 text-cream max-w-md rounded-2xl p-0 overflow-hidden"
                >
                    <div className="p-7 sm:p-9">
                        <DialogTitle className="font-serif text-3xl sm:text-4xl leading-tight">
                            {t("orderDialog.title1")} <span className="gold-text italic">{t("orderDialog.title2")}</span>
                        </DialogTitle>
                        <DialogDescription className="text-stone-400 text-sm mt-2">
                            {t("orderDialog.desc")}
                        </DialogDescription>

                        <div className="grid grid-cols-2 gap-3 mt-7">
                            <div className="relative gold-border rounded-xl p-4 bg-elevated/60 opacity-80" data-testid="order-choice-delivery">
                                <span className="absolute top-3 right-3 rounded-full bg-saffron/15 border border-saffron/40 px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase text-saffron-glow">
                                    {t("notice.soon")}
                                </span>
                                <Truck className="w-6 h-6 text-saffron-bright" strokeWidth={1.5} />
                                <p className="font-serif text-xl mt-3">{t("orderDialog.delT")}</p>
                                <p className="text-xs text-stone-400 mt-1">{t("orderDialog.delS")}</p>
                            </div>
                            <div className="gold-border rounded-xl p-4 bg-elevated/60" data-testid="order-choice-pickup">
                                <ShoppingBag className="w-6 h-6 text-saffron-bright" strokeWidth={1.5} />
                                <p className="font-serif text-xl mt-3">{t("orderDialog.pickT")}</p>
                                <p className="text-xs text-stone-400 mt-1">{t("orderDialog.pickS")}</p>
                            </div>
                        </div>

                        <div className="mt-5 space-y-2.5 text-xs text-stone-300">
                            <p className="flex items-center gap-2">
                                <BadgePercent className="w-4 h-4 text-brass shrink-0" />
                                {t("orderDialog.promo1")}
                            </p>
                            <p className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-brass shrink-0" />
                                {t("orderDialog.promo2")}
                            </p>
                            <p className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-brass shrink-0" />
                                {t("footer.hours")}
                            </p>
                        </div>

                        <motion.a
                            data-testid="order-dialog-call-button"
                            href={SITE.phoneHref}
                            whileTap={{ scale: 0.97 }}
                            className="mt-7 flex items-center justify-center gap-2.5 w-full rounded-full bg-saffron px-6 py-4 text-ink font-semibold text-sm tracking-wide hover:bg-saffron-bright transition-colors duration-300"
                        >
                            <Phone className="w-4 h-4" />
                            {t("orderDialog.call")} · {SITE.phoneDisplay}
                        </motion.a>
                    </div>
                </DialogContent>
            </Dialog>
        </OrderContext.Provider>
    );
};

export const OrderButton = ({ children = "ORDER ONLINE", className = "", testId = "order-online-button" }) => {
    const order = useOrder();
    return (
        <motion.button
            data-testid={testId}
            onClick={() => order?.openOrder()}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`rounded-full bg-saffron text-ink font-semibold tracking-wide hover:bg-saffron-bright hover:shadow-[0_0_36px_rgba(217,119,6,0.35)] transition-[background-color,box-shadow] duration-300 ${className}`}
        >
            {children}
        </motion.button>
    );
};
